import React from 'react';
import { motion } from 'framer-motion';
import { Youtube, Facebook, Instagram, Twitter, Music2, ExternalLink } from 'lucide-react';

// Social Media Links - Update these with actual URLs
const SOCIAL_LINKS = {
  youtube: {
    url: 'https://www.youtube.com/channel/UCZ8ewM6AKFbobbU6TscphoA',
    name: 'YouTube',
    icon: Youtube,
    color: '#FF0000',
    bgColor: 'bg-red-500',
    hoverBg: 'hover:bg-red-600',
    followers: 'Subscribe'
  },
  facebook: {
    url: 'https://www.facebook.com/profile.php?id=61579689947073',
    name: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    bgColor: 'bg-blue-600',
    hoverBg: 'hover:bg-blue-700',
    followers: 'Follow'
  },
  instagram: {
    url: 'https://www.instagram.com/livingfruitministry/',
    name: 'Instagram',
    icon: Instagram,
    color: '#E4405F',
    bgColor: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
    hoverBg: 'hover:opacity-90',
    followers: 'Follow'
  },
  tiktok: {
    url: 'https://www.tiktok.com/@livingfruitministry',
    name: 'TikTok',
    icon: Music2,
    color: '#000000',
    bgColor: 'bg-black',
    hoverBg: 'hover:bg-gray-800',
    followers: 'Follow'
  },
  twitter: {
    url: 'https://x.com/LFMorg',
    name: 'X',
    icon: Twitter,
    color: '#1DA1F2',
    bgColor: 'bg-sky-500',
    hoverBg: 'hover:bg-sky-600',
    followers: 'Follow'
  },
  spotify: {
    url: 'https://open.spotify.com/show/0uRoGxumk0eCNdLs0TJCDP',
    name: 'Spotify',
    icon: Music2,
    color: '#1DB954',
    bgColor: 'bg-green-500',
    hoverBg: 'hover:bg-green-600',
    followers: 'Listen'
  }
};

// Button style variants
const VARIANTS = {
  // Full buttons with text
  full: ({ platform, index }) => {
    const Icon = platform.icon;
    return (
      <motion.a
        key={platform.name}
        href={platform.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`flex items-center gap-3 px-6 py-3 rounded-full text-white font-medium transition-all ${platform.bgColor} ${platform.hoverBg} ${!platform.url && 'opacity-50 cursor-not-allowed'}`}
        onClick={(e) => !platform.url && e.preventDefault()}
      >
        <Icon size={20} />
        <span>{platform.followers} on {platform.name}</span>
      </motion.a>
    );
  },
  
  // Icon only (compact)
  icon: ({ platform, index }) => {
    const Icon = platform.icon;
    return (
      <motion.a
        key={platform.name}
        href={platform.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: index * 0.05 }}
        className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-all ${platform.bgColor} ${platform.hoverBg} ${!platform.url && 'opacity-30 cursor-not-allowed'}`}
        onClick={(e) => !platform.url && e.preventDefault()}
        title={platform.url ? `Follow on ${platform.name}` : `${platform.name} coming soon`}
      >
        <Icon size={18} />
      </motion.a>
    );
  },
  
  // Outlined style
  outlined: ({ platform, index }) => {
    const Icon = platform.icon;
    return (
      <motion.a
        key={platform.name}
        href={platform.url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all hover:text-white ${!platform.url && 'opacity-40 cursor-not-allowed'}`}
        style={{ 
          borderColor: platform.color, 
          color: platform.color,
        }}
        onMouseEnter={(e) => {
          if (platform.url) {
            e.currentTarget.style.backgroundColor = platform.color;
            e.currentTarget.style.color = 'white';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = platform.color;
        }}
        onClick={(e) => !platform.url && e.preventDefault()}
      >
        <Icon size={18} />
        <span className="text-sm font-medium">{platform.name}</span>
      </motion.a>
    );
  }
};

export default function SocialFollowButtons({ 
  variant = 'full', // 'full', 'icon', 'outlined'
  platforms = ['youtube', 'facebook', 'instagram', 'spotify'], // Which platforms to show
  className = '',
  title = 'Follow Us',
  showTitle = true
}) {
  const filteredPlatforms = platforms
    .map(p => SOCIAL_LINKS[p])
    .filter(Boolean);

  const renderButton = VARIANTS[variant] || VARIANTS.full;

  return (
    <div className={`${className}`} data-testid="social-follow-buttons">
      {showTitle && (
        <h3 className="text-lg font-serif text-charcoal mb-4">{title}</h3>
      )}
      <div className={`flex flex-wrap gap-3 ${variant === 'icon' ? 'gap-2' : 'gap-3'}`}>
        {filteredPlatforms.map((platform, index) => renderButton({ platform, index }))}
      </div>
    </div>
  );
}

// Export social links for use elsewhere
export { SOCIAL_LINKS };
